import React, { useMemo } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'
import { type SchedulingEventType } from '../../../models/Scheduling'
import { type User } from '../../../models/User'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { useGetSchedulingEventTypesQuery, useGetSchedulingUsersQuery } from '../../../store/scheduling/scheduling'
import { getUserDisplayName } from '../../../apps/professional-app/fa/common/aifeatures/utils'
import { Spinner } from '../../../components/Spinner/Spinner'
import { EventTypeSelect } from '../../Scheduling/flows/form/EventTypeSelect'
import { hostsWithoutEventType, isOwnerBookable, type HostSelection, type HostWithoutEventType } from './eventTypeCoverage'
import { type SendBookingInvitationsOwners } from './useSendBookingInvitationsOwners'
import { FormSection } from './FormSection'
import { SkippedClientsPopover, type SkippedClientsGroup } from './SkippedClientsPopover'
import {
  EVENT_TYPE_DESCRIPTION,
  EVENT_TYPE_HOSTS_ERROR,
  EVENT_TYPE_LOAD_ERROR,
  EVENT_TYPE_TITLE,
  UNKNOWN_USER,
  EVENT_TYPE_OWNER_YOU,
  eventTypeNotTurnedOn,
  eventTypeSkipped,
  skippedGroupOwnerOff,
} from './sendBookingInvitationsCopy'

const EMPTY_EVENT_TYPES: SchedulingEventType[] = []

interface StatusLineProps {
  hostsOff: HostWithoutEventType[]
  eventTypeName: string
  nameOf: (userId: string) => string
  clientNameOf: (clientId: string) => string
  resultsLinkFor?: (clientIds: string[]) => (() => void) | undefined
}

const StatusLine: React.FC<StatusLineProps> = ({ hostsOff, eventTypeName, nameOf, clientNameOf, resultsLinkFor }) => {
  const currentUser = useAppSelector(selectSafeAuthenticatedUser)
  if (hostsOff.length === 0) {
    return null
  }
  const skippedCount = hostsOff.reduce((total, host) => total + host.clientCount, 0)
  const ownerNames = hostsOff.map((host) => (host.userId === currentUser.id ? EVENT_TYPE_OWNER_YOU : nameOf(host.userId)))
  const groups: SkippedClientsGroup[] = [
    {
      key: 'ownerOff',
      title: skippedGroupOwnerOff(eventTypeName),
      clients: hostsOff.flatMap((host) => host.clientIds.map((clientId) => ({ id: clientId, name: clientNameOf(clientId), detail: nameOf(host.userId) }))),
    },
  ]

  return (
    <div className='d-flex align-items-start gap-2 p-3 border rounded-3 zocks-border-color-gray-200 zocks-bg-color-gray-100 paragraph-small bold zocks-text-gray-700'>
      <i className='bi bi-exclamation-circle zocks-text-gray-900' aria-hidden='true' />
      <span>
        {eventTypeNotTurnedOn(ownerNames)} {eventTypeSkipped(skippedCount)}
        {hostsOff.length < 3 ? null : (
          <>
            {' '}
            <SkippedClientsPopover groups={groups} onOpenInResults={resultsLinkFor?.(hostsOff.flatMap((host) => host.clientIds))} />
          </>
        )}
      </span>
    </div>
  )
}

interface EventTypeSectionProps {
  owners: SendBookingInvitationsOwners
  hostSelection: HostSelection
  eventTypeId: string | null
  onEventTypeIdChange: (eventTypeId: string) => void
  resultsLinkFor?: (clientIds: string[]) => (() => void) | undefined
}

export const EventTypeSection: React.FC<EventTypeSectionProps> = ({ owners, hostSelection, eventTypeId, onEventTypeIdChange, resultsLinkFor }) => {
  const accountId = useAppSelector(selectAccountId)
  const { data: eventTypes, isLoading: eventTypesLoading, isError: eventTypesError } = useGetSchedulingEventTypesQuery({ accountId })
  const {
    data: enabledUsers,
    isFetching: enabledUsersFetching,
    isError: enabledUsersError,
  } = useGetSchedulingUsersQuery(eventTypeId ? { accountId, eventTypeId } : skipToken)

  const bookableEventTypes = useMemo(() => (eventTypes ?? EMPTY_EVENT_TYPES).filter(isOwnerBookable), [eventTypes])

  const enabledUserIds = useMemo(() => (enabledUsers ? new Set(enabledUsers.map((user) => user.id)) : null), [enabledUsers])

  const teammatesById = useMemo(() => new Map<string, User>(owners.teammates.map((teammate) => [teammate.id, teammate])), [owners.teammates])
  const nameOf = (userId: string): string => {
    const teammate = teammatesById.get(userId)
    return teammate ? getUserDisplayName(teammate) : UNKNOWN_USER
  }
  const clientsById = new Map<string, User>(owners.clients.map((client) => [client.id, client]))
  const clientNameOf = (clientId: string): string => {
    const client = clientsById.get(clientId)
    return client ? getUserDisplayName(client) : UNKNOWN_USER
  }

  const status = (() => {
    if (!eventTypeId || owners.isLoading || owners.isError) {
      return null
    }
    if (enabledUsersFetching) {
      return <Spinner size='sm' />
    }
    if (enabledUsersError) {
      return <span className='paragraph-small zocks-text-gray-700'>{EVENT_TYPE_HOSTS_ERROR}</span>
    }
    return enabledUserIds ? (
      <StatusLine
        hostsOff={hostsWithoutEventType(owners.ownerIdByClientId, hostSelection, enabledUserIds)}
        eventTypeName={eventTypes?.find((candidate) => candidate.id === eventTypeId)?.name ?? ''}
        nameOf={nameOf}
        clientNameOf={clientNameOf}
        resultsLinkFor={resultsLinkFor}
      />
    ) : null
  })()

  const content = (() => {
    if (eventTypesLoading) {
      return (
        <div className='d-flex justify-content-center py-3'>
          <Spinner />
        </div>
      )
    }
    if (eventTypesError) {
      return <span className='paragraph-small zocks-text-gray-700'>{EVENT_TYPE_LOAD_ERROR}</span>
    }
    return (
      <>
        <EventTypeSelect
          eventTypes={bookableEventTypes}
          selectedEventTypeId={eventTypeId}
          onEventTypeChange={(eventType) => onEventTypeIdChange(eventType.id)}
          label={EVENT_TYPE_TITLE}
          labelClassName='visually-hidden'
          description={null}
        />
        {status}
      </>
    )
  })()

  return (
    <FormSection title={EVENT_TYPE_TITLE} description={EVENT_TYPE_DESCRIPTION}>
      {content}
    </FormSection>
  )
}
